import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { qk } from './queryKeys';
import {
  clearCopyOverride,
  getCopyBundle,
  getCopyKey,
  getCopyPending,
  listCopyBundles,
  listCopyCatalog,
  publishCopy,
  revertCopyBundle,
  saveCopyDraft,
  syncCopyCatalog,
} from '@/api/admin/copy';
import {
  DEFAULT_COPY_CATALOG_PAGE_SIZE,
  type CopyCatalogFilters,
  type CopyDraftUpsert,
  type CopyFlagFilter,
  type CopyLocale,
  type CopyPublishRequest,
  type CopySyncRequest,
} from '@/schemas/admin/copy';

const BUNDLE_PAGE_SIZE = 25;

/**
 * Every copy mutation can move the catalog, the pending count and the bundle
 * history at once (a publish promotes drafts *and* mints a bundle), so they all
 * invalidate together — the same discipline as `invalidateHomepage`.
 */
export function invalidateCopy(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['copy'] });
  void qc.invalidateQueries({ queryKey: qk.audit() });
}

/**
 * Catalog paging is server-side (624 keys, keyset cursor on `key ASC`) and so
 * is search — filtering the loaded page in the browser would make a search for
 * "safety" miss everything past the first page.
 */
export function useCopyCatalog(filters: CopyCatalogFilters) {
  const withLimit: CopyCatalogFilters = {
    ...filters,
    limit: filters.limit ?? DEFAULT_COPY_CATALOG_PAGE_SIZE,
  };
  return useInfiniteQuery({
    queryKey: qk.copyCatalog(withLimit),
    queryFn: ({ pageParam, signal }) =>
      listCopyCatalog(pageParam ? { ...withLimit, cursor: pageParam } : withLimit, signal),
    initialPageParam: '',
    getNextPageParam: (last) => (last.next_cursor ? last.next_cursor : undefined),
    staleTime: 15_000,
  });
}

/**
 * How many keys carry a flag. The catalog endpoint has no aggregate count, so
 * this asks for one page of the flag's own filter and reports `100+` when the
 * cursor says there are more — the server's answer, paginated, never a
 * client-side derivation that could disagree with it.
 */
export function useCopyFlagCount(flag: CopyFlagFilter, enabled = true) {
  return useQuery({
    queryKey: qk.copyFlagCount(flag),
    queryFn: ({ signal }) => listCopyCatalog({ flag, limit: 100 }, signal),
    enabled,
    staleTime: 60_000,
    // A row the console cannot parse still carries the flag.
    select: (res) => ({
      count: res.items.length + res.invalid.length,
      more: res.next_cursor !== '',
    }),
  });
}

export function useCopyKey(key: string | null) {
  return useQuery({
    queryKey: qk.copyKey(key ?? ''),
    queryFn: ({ signal }) => getCopyKey(key ?? '', signal),
    enabled: Boolean(key),
    staleTime: 5_000,
  });
}

export function useCopyPending() {
  return useQuery({
    queryKey: qk.copyPending(),
    queryFn: ({ signal }) => getCopyPending(signal),
    staleTime: 5_000,
  });
}

export function useCopyBundles() {
  return useInfiniteQuery({
    queryKey: qk.copyBundles(),
    queryFn: ({ pageParam, signal }) =>
      listCopyBundles(
        pageParam ? { limit: BUNDLE_PAGE_SIZE, cursor: pageParam } : { limit: BUNDLE_PAGE_SIZE },
        signal
      ),
    initialPageParam: '',
    getNextPageParam: (last) => (last.next_cursor ? last.next_cursor : undefined),
    staleTime: 15_000,
  });
}

export function useCopyBundle(version: number | null) {
  return useQuery({
    queryKey: qk.copyBundle(version ?? 0),
    queryFn: ({ signal }) => getCopyBundle(version ?? 0, signal),
    enabled: version != null,
    staleTime: Infinity, // bundles are immutable once minted
  });
}

export function useSaveCopyDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      locale,
      input,
    }: {
      key: string;
      locale: CopyLocale;
      input: CopyDraftUpsert;
    }) => saveCopyDraft(key, locale, input),
    onSuccess: () => invalidateCopy(qc),
  });
}

export function useClearCopyOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, locale }: { key: string; locale: CopyLocale }) =>
      clearCopyOverride(key, locale),
    onSuccess: () => invalidateCopy(qc),
  });
}

export function usePublishCopy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CopyPublishRequest) => publishCopy(input),
    onSuccess: () => invalidateCopy(qc),
  });
}

export function useRevertCopyBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, note }: { version: number; note?: string }) =>
      revertCopyBundle(version, note ? { note } : {}),
    onSuccess: () => invalidateCopy(qc),
  });
}

/** A committed sync rewrites the catalog, so every copy view refetches. */
export function useSyncCopyCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CopySyncRequest = {}) => syncCopyCatalog(input),
    onSuccess: (report) => {
      if (!report.dry_run) invalidateCopy(qc);
    },
  });
}
